import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CheckSquare, Lock } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (pwd.length < 6) return setError("Password must be at least 6 characters");
    if (pwd !== pwd2) return setError("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      toast.success("Password reset — please sign in.");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] flex items-center justify-center auth-bg grain-bg p-6" style={{ paddingTop: "max(1.5rem, env(safe-area-inset-top))", paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }}>
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-md bg-primary flex items-center justify-center">
            <CheckSquare size={20} className="text-primary-foreground" />
          </div>
          <span className="font-display font-extrabold text-lg">FlowTasks<span className="text-primary">360</span></span>
        </div>
        <h2 className="font-display text-3xl font-bold mb-2">Choose a new password</h2>
        <p className="text-sm text-muted-foreground mb-7">Enter a new password for your account.</p>

        {!token && (
          <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2 mb-4">
            Missing or invalid reset token.
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-form">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">New password</Label>
            <div className="relative mt-1.5">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="reset-pwd-input"
                type="password"
                required
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                className="pl-9 h-11"
                placeholder="At least 6 characters"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Confirm password</Label>
            <div className="relative mt-1.5">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="reset-pwd2-input"
                type="password"
                required
                value={pwd2}
                onChange={(e) => setPwd2(e.target.value)}
                className="pl-9 h-11"
              />
            </div>
          </div>
          {error && (
            <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">{error}</div>
          )}
          <Button type="submit" disabled={loading || !token} className="w-full h-11" data-testid="reset-submit-btn">
            {loading ? "Resetting…" : "Reset password"}
          </Button>
          <p className="text-sm text-muted-foreground text-center">
            <Link to="/login" className="hover:underline">Back to sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
